import { isMatch, merge, pick } from 'lodash';
import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import usePersistent from 'hooks/use-persistent';
import { ROUTES } from 'pages/Search/constants';
import { noneIf } from 'pages/Search/utils';

interface State {
    page: number;
    size: number;
    cvss?: string;
    sort?: {
        field: 'vendor' | 'product' | 'cves';
        direction: 'asc' | 'desc';
    };
}

const defaults: State = {
    page: 1,
    size: 20,
    cvss: undefined,
    sort: {
        field: 'cves',
        direction: 'desc'
    }
};

export function useCvssProductCatalogState() {
    const history = useHistory();
    const location = useLocation();
    const parameters = useParams<{ cvss: string; page: string }>();
    const query = useMemo(() => qs.parse(location.search, { ignoreQueryPrefix: true }), [location.search]);

    const page = parameters.page ? parseInt(parameters.page) : defaults.page;
    const cvss = parameters.cvss ?? defaults.cvss;
    const size = query.size ? parseInt(query.size.toString()) : defaults.size;
    const sort = query.sort || defaults.sort;

    const state: State = useMemo(() => ({ page, size, cvss, sort } as State), [page, size, cvss, sort]);
    const stateRef = usePersistent(state);

    const url = useCallback(
        (patch: Partial<State>) => {
            const previous = stateRef.current;
            const current = merge({}, previous, patch);

            return ROUTES.CVSS({
                cvss: current.cvss,
                page: isMatch(previous, pick(current, ['score', 'size']))
                    ? noneIf(current.page, defaults.page)
                    : undefined,
                size: noneIf(current.size, previous.size),
                sort: noneIf(current.sort, defaults.sort)
            });
        },
        [stateRef]
    );

    const set = useCallback((patch: Partial<State>) => history.push(url(patch)), [history, url]);

    const actions = useMemo(() => ({ url, set }), [set, url]);

    return [state, actions] as const;
}
